/**
 * フォームビルダ（フォームの中身 = 何を訊くか + プレビュー）。
 * 仕様書: docs/01_spec/spec_line_bot.md A-12.4（Phase 24b 拡張）
 *
 * 2026-07-28: 受付条件・営業連携・顧客反映などの設定は「設定」タブ
 * (FormSettingsPage) へ移設。この画面は表示内容と要素定義に専念する。
 * 保存ペイロードは useFormEditor / buildFormUpdatePayload に一本化されている。
 */
import {
  CheckCircle2,
  Copy,
  ExternalLink,
  Eye,
  FileText,
  Image as ImageIcon,
  Save,
  Tag as TagIcon,
  Trash2,
  X,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import { ImageGalleryDialog } from '@/components/common/ImageGalleryDialog';
import { Screen } from '@/components/layout/Screen';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input, Label, Textarea } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Spinner } from '@/components/ui/spinner';
import { toast } from '@/components/ui/toast';
import { type FormRecord } from '@/hooks/useForms';
import { useMailContacts } from '@/hooks/useMailContacts';

import { FormElementEditor } from './FormElementEditor';
import { FormPageTabs } from './FormPageTabs';
import { FormPreview } from './FormPreview';
import {
  SettingDetails,
  SettingsSection,
} from './settings/SettingsPrimitives';
import { useFormEditor } from './useFormEditor';

import { type FormStatus } from '@goenhub/shared';

const STATUS_OPTIONS: Array<{ value: FormStatus; label: string }> = [
  { value: 'draft', label: '下書き' },
  { value: 'published', label: '公開中' },
  { value: 'closed', label: '受付終了' },
];

export function FormBuilderPage() {
  const { id } = useParams<{ id: string }>();
  const { form, isLoading, dirty, saving, update, save } = useFormEditor(id);
  const { data: allContacts } = useMailContacts({ limit: 500 });
  const [logoGalleryOpen, setLogoGalleryOpen] = useState(false);
  const [tagDraft, setTagDraft] = useState('');

  // 既存 mail_contacts.tags の出現値を distinct で抽出（サジェスト用）
  const existingTagSuggestions = useMemo(() => {
    const set = new Set<string>();
    for (const c of allContacts ?? []) {
      for (const t of c.tags ?? []) {
        const trimmed = t.trim();
        if (trimmed) set.add(trimmed);
      }
    }
    return Array.from(set).sort();
  }, [allContacts]);

  if (isLoading || !form) {
    return (
      <div className="flex items-center justify-center p-12">
        <Spinner />
      </div>
    );
  }

  const addContactTag = (raw: string) => {
    const t = raw.trim();
    if (!t) return;
    const current = form.contactTags ?? [];
    if (current.includes(t)) return;
    update({ contactTags: [...current, t] });
  };

  const removeContactTag = (t: string) => {
    update({
      contactTags: (form.contactTags ?? []).filter((x) => x !== t),
    });
  };

  const handleTagDraftKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      if (tagDraft.trim()) {
        addContactTag(tagDraft);
        setTagDraft('');
      }
    } else if (e.key === 'Backspace' && !tagDraft) {
      const current = form.contactTags ?? [];
      if (current.length > 0) {
        removeContactTag(current[current.length - 1]);
      }
    }
  };

  const publicUrl = `${window.location.origin}/public/forms/${id}`;

  const handleCopyUrl = async () => {
    try {
      await navigator.clipboard.writeText(publicUrl);
      toast.success('公開 URL をコピーしました');
    } catch {
      toast.error('コピーに失敗しました');
    }
  };

  return (
    <Screen
      title={`フォームビルダー — ${form.name}`}
      description="左側で編集 → 右側でプレビュー"
      backTo="/campaigns?hub=forms"
      actions={
        <Button
          type="button"
          variant="primary"
          size="sm"
          onClick={() => void save()}
          disabled={!dirty || saving}
        >
          {saving ? <Spinner className="size-3 mr-1" /> : <Save className="size-3.5 mr-1" />}
          {dirty ? '保存' : '保存済'}
        </Button>
      }
    >
      {id && (
        <FormPageTabs
          formId={id}
          submissionCount={form.submissionCount ?? 0}
          active="builder"
          dirty={dirty}
        />
      )}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
        {/* === 左：編集 ===
            並び順は公開ページの見え方に合わせている:
              ① 基本情報（社内向け・公開URL） → ② 公開ページの見た目 →
              ③ 質問（主作業） → ④ 送信完了後 → ⑤ 顧客タグ
            旧 UI はメタ情報と URL 注記が上部を占有し、主作業の要素定義まで
            スクロールが必要だったため、上部を畳んで要素定義を前に出した。 */}
        <div className="space-y-4">
          {/* ① 基本情報（社内向け） */}
          <SettingsSection
            icon={FileText}
            title="基本情報"
            description="社内でこのフォームを管理するための情報です。"
            summary={
              <Badge
                variant={
                  form.status === 'published'
                    ? 'approved'
                    : form.status === 'closed'
                      ? 'default'
                      : 'draft'
                }
              >
                {form.status === 'published'
                  ? '公開中'
                  : form.status === 'closed'
                    ? '受付終了'
                    : '下書き'}
              </Badge>
            }
          >
            <div className="p-4 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">管理画面表示名</Label>
                  <Input
                    type="text"
                    value={form.name}
                    onChange={(e) => update({ name: e.target.value })}
                    placeholder="例: 4月キャンペーン申込"
                  />
                  <p className="text-[10px] text-[var(--text-muted)] mt-1">
                    一覧で探すための社内用の名前。お客様には表示されません。
                  </p>
                </div>
                <div>
                  <Label className="text-xs">ステータス</Label>
                  <Select
                    value={form.status}
                    onValueChange={(v) => update({ status: v as FormStatus })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {STATUS_OPTIONS.map((s) => (
                        <SelectItem key={s.value} value={s.value}>
                          {s.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-[10px] text-[var(--text-muted)] mt-1">
                    「公開中」にすると URL からアクセスできます。
                  </p>
                </div>
              </div>

              <div>
                <Label className="text-xs">公開 URL</Label>
                <div className="flex items-center gap-2">
                  <Input
                    type="text"
                    readOnly
                    value={publicUrl}
                    className="font-mono text-xs"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={handleCopyUrl}
                    title="コピー"
                  >
                    <Copy className="size-3.5" />
                  </Button>
                  {form.status === 'published' && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() =>
                        window.open(publicUrl, '_blank', 'noopener')
                      }
                      title="公開ページを開く"
                    >
                      <ExternalLink className="size-3.5" />
                    </Button>
                  )}
                </div>
                <div className="mt-2">
                  <PublicAccessHint
                    form={form}
                    onPublish={() => update({ status: 'published' })}
                  />
                </div>
                <SettingDetails label="URL について">
                  <p>
                    キャンペーンと連動させると{' '}
                    <code>?campaignId=&#123;X&#125;</code> が自動で付きます。
                  </p>
                  <p>
                    現在の環境:{' '}
                    <span className="font-mono text-[var(--text-secondary)]">
                      {import.meta.env.VITE_FIREBASE_PROJECT_ID ?? '(unknown)'}
                    </span>
                    。この URL は同じ環境のフォームだけを表示します。
                  </p>
                </SettingDetails>
              </div>
            </div>
          </SettingsSection>

          {/* ② 公開ページの見た目 */}
          <SettingsSection
            icon={Eye}
            title="公開ページの見た目"
            description="お客様に表示される見出しと配色です。右のプレビューに即反映されます。"
          >
            <div className="p-4 space-y-3">
              <div>
                <Label className="text-xs">タイトル</Label>
                <Input
                  type="text"
                  value={form.title}
                  onChange={(e) => update({ title: e.target.value })}
                  placeholder="例: ご来場予約フォーム"
                />
                <p className="text-[10px] text-[var(--text-muted)] mt-1">
                  ページ最上部の大見出し。お客様に表示されます。
                </p>
              </div>

              <div>
                <Label className="text-xs">説明（任意）</Label>
                <Textarea
                  value={form.description ?? ''}
                  onChange={(e) => update({ description: e.target.value })}
                  rows={2}
                  placeholder="フォームの趣旨や記入の注意点など"
                />
              </div>

              <div className="grid grid-cols-2 gap-3 pt-1">
                <div>
                  <Label className="text-xs">ブランドカラー</Label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={form.theme?.brandColor ?? '#4a7cff'}
                      onChange={(e) =>
                        update({
                          theme: { ...form.theme, brandColor: e.target.value },
                        })
                      }
                      className="h-8 w-12 rounded border border-[var(--border)] bg-transparent cursor-pointer"
                    />
                    <span className="text-[11px] font-mono text-[var(--text-secondary)]">
                      {form.theme?.brandColor ?? '#4a7cff'}
                    </span>
                  </div>
                  <p className="text-[10px] text-[var(--text-muted)] mt-1">
                    タイトル文字・送信ボタンの色
                  </p>
                </div>
                <div>
                  <div className="flex items-center justify-between gap-2 mb-1.5">
                    <Label className="text-xs !mb-0">フォーム背景色</Label>
                    {form.theme?.backgroundColor && (
                      <button
                        type="button"
                        className="text-[10px] text-[var(--text-secondary)] underline"
                        onClick={() =>
                          update({
                            theme: { ...form.theme, backgroundColor: '' },
                          })
                        }
                      >
                        白に戻す
                      </button>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={form.theme?.backgroundColor || '#ffffff'}
                      onChange={(e) =>
                        update({
                          theme: {
                            ...form.theme,
                            backgroundColor: e.target.value,
                          },
                        })
                      }
                      className="h-8 w-12 rounded border border-[var(--border)] bg-transparent cursor-pointer"
                    />
                    <span className="text-[11px] font-mono text-[var(--text-secondary)]">
                      {form.theme?.backgroundColor || '#ffffff（既定）'}
                    </span>
                  </div>
                  <p className="text-[10px] text-[var(--text-muted)] mt-1">
                    入力欄が乗るカードの背景色
                  </p>
                </div>
              </div>

              <div>
                <Label className="text-xs">ロゴ画像（任意）</Label>
                {form.theme?.logoUrl ? (
                  <div className="flex items-center gap-3">
                    <img
                      src={form.theme.logoUrl}
                      alt=""
                      className="max-h-12 rounded border border-[var(--border)] bg-white p-1.5"
                    />
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        onClick={() => setLogoGalleryOpen(true)}
                      >
                        <ImageIcon className="size-3.5 mr-1" />
                        変更
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() =>
                          update({ theme: { ...form.theme, logoUrl: '' } })
                        }
                      >
                        <Trash2 className="size-3.5 mr-1" />
                        削除
                      </Button>
                    </div>
                  </div>
                ) : (
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={() => setLogoGalleryOpen(true)}
                  >
                    <ImageIcon className="size-3.5 mr-1" />
                    ロゴをアップロード
                  </Button>
                )}
              </div>
            </div>
          </SettingsSection>

          {/* ③ 質問（主作業） — 自前の見出しを持つのでセクションヘッダーは付けない */}
          <div className="rounded-lg border border-[var(--border)] bg-[var(--bg-surface)] p-4">
            <FormElementEditor
              elements={form.elements}
              onChange={(elements) => update({ elements })}
            />
          </div>

          {/* ④ 送信完了後 */}
          <SettingsSection
            icon={CheckCircle2}
            title="送信完了後"
            description="送信ボタンを押したお客様に何を見せるか。"
          >
            <div className="p-4 space-y-3">
              <div>
                <Label className="text-xs">完了メッセージ</Label>
                <Textarea
                  value={form.thanksMessage ?? ''}
                  onChange={(e) => update({ thanksMessage: e.target.value })}
                  rows={2}
                  placeholder="ご回答ありがとうございました。"
                />
              </div>
              <div>
                <Label className="text-xs">リダイレクト URL（任意）</Label>
                <Input
                  type="url"
                  value={form.redirectUrl ?? ''}
                  onChange={(e) => update({ redirectUrl: e.target.value })}
                  placeholder="https://..."
                />
                <p className="text-[10px] text-[var(--text-muted)] mt-1">
                  指定すると、完了メッセージの代わりにこの URL へ自動遷移します。
                </p>
              </div>
            </div>
          </SettingsSection>

          {/* ⑤ 送信者に付与するタグ */}
          <SettingsSection
            icon={TagIcon}
            title="送信者に付けるタグ"
            description="回答した人を、あとからメール配信の宛先として絞り込むための目印です。"
            summary={
              (form.contactTags ?? []).length > 0 ? (
                <Badge variant="accent">
                  {(form.contactTags ?? []).length} 個
                </Badge>
              ) : (
                <span className="text-[var(--text-muted)]">なし</span>
              )
            }
          >
            <div className="p-4 space-y-2">
              <div className="flex flex-wrap items-center gap-1.5 rounded border border-[var(--border)] bg-[var(--bg-surface)] p-2 min-h-[40px]">
                {(form.contactTags ?? []).map((t) => (
                  <span
                    key={t}
                    className="inline-flex items-center gap-1 rounded bg-[var(--accent-subtle)] text-[var(--accent)] text-[11px] font-semibold px-2 py-0.5"
                  >
                    {t}
                    <button
                      type="button"
                      onClick={() => removeContactTag(t)}
                      className="hover:opacity-70"
                      aria-label={`${t} を削除`}
                    >
                      <X className="size-3" />
                    </button>
                  </span>
                ))}
                <input
                  type="text"
                  value={tagDraft}
                  onChange={(e) => setTagDraft(e.target.value)}
                  onKeyDown={handleTagDraftKeyDown}
                  onBlur={() => {
                    if (tagDraft.trim()) {
                      addContactTag(tagDraft);
                      setTagDraft('');
                    }
                  }}
                  placeholder={
                    (form.contactTags ?? []).length === 0
                      ? '例: 4月キャンペーン応募者　Enter または , で確定'
                      : '追加…'
                  }
                  className="flex-1 min-w-[120px] bg-transparent text-[13px] outline-none placeholder:text-[var(--text-muted)]"
                />
              </div>
              {existingTagSuggestions.length > 0 && (
                <div>
                  <p className="text-[10px] text-[var(--text-muted)] mb-1">
                    既存タグから追加：
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {existingTagSuggestions
                      .filter((t) => !(form.contactTags ?? []).includes(t))
                      .slice(0, 30)
                      .map((t) => (
                        <button
                          key={t}
                          type="button"
                          onClick={() => addContactTag(t)}
                          className="rounded border border-[var(--border)] bg-[var(--bg-surface)] text-[var(--text-secondary)] text-[11px] px-2 py-0.5 hover:border-[var(--accent)] hover:text-[var(--accent)] transition-colors"
                        >
                          + {t}
                        </button>
                      ))}
                  </div>
                </div>
              )}
              <SettingDetails>
                <p>
                  タグは送信完了時に、回答者の顧客レコードへ自動で追加されます（重複は自動で除かれます）。
                </p>
                <p>
                  「このフォームに答えた人」「このキャンペーン経由の人」で絞り込むだけなら、タグを付けなくても自動で記録されています。宛先の絞り込みはそちらが確実です。タグは横断的な属性（例: 経営者、TW所有者）に使ってください。
                </p>
              </SettingDetails>
            </div>
          </SettingsSection>
        </div>

        {/* === 右：プレビュー === */}
        <div className="lg:sticky lg:top-4">
          <FormPreview form={form} formId={id} />
        </div>
      </div>

      {/* ロゴギャラリーモーダル */}
      <ImageGalleryDialog
        open={logoGalleryOpen}
        onOpenChange={setLogoGalleryOpen}
        title="ロゴ画像を選択"
        onSelect={(url) =>
          update({ theme: { ...form.theme, logoUrl: url } })
        }
      />
    </Screen>
  );
}

/**
 * 公開アクセス状態の診断ヒント。
 * バックエンド (functions/src/forms/submit.ts:checkPublicAccess) と同じ条件で
 * 「なぜ公開されないか」をユーザに見せる。
 */
function PublicAccessHint({
  form,
  onPublish,
}: {
  form: FormRecord;
  onPublish: () => void;
}) {
  const tsToMs = (v: unknown): number | null => {
    if (!v) return null;
    if (typeof v === 'object' && v && 'toMillis' in v) {
      try {
        return (v as { toMillis(): number }).toMillis();
      } catch {
        return null;
      }
    }
    if (typeof v === 'string') {
      const n = Date.parse(v);
      return Number.isNaN(n) ? null : n;
    }
    return null;
  };
  const now = Date.now();
  const startMs = tsToMs(form.publicStartAt);
  const endMs = tsToMs(form.publicEndAt);

  // 1) ステータスが公開中でない
  if (form.status !== 'published') {
    return (
      <div className="rounded border border-[var(--accent-amber)] bg-[var(--accent-amber-subtle)] p-2 flex items-center justify-between gap-2 text-[11px]">
        <div className="text-[var(--accent-amber)] font-semibold">
          ⚠️ 公開ページにアクセスしても「
          {form.status === 'closed' ? '受付終了' : '受付終了'}
          」表示になります
        </div>
        <button
          type="button"
          onClick={onPublish}
          className="px-2 py-1 rounded-sm bg-[var(--accent)] text-white text-[11px] font-semibold hover:opacity-90"
        >
          ステータスを「公開中」に変更
        </button>
      </div>
    );
  }

  // 2) 公開開始時刻が未来
  if (startMs && now < startMs) {
    const hours = Math.ceil((startMs - now) / 3_600_000);
    return (
      <div className="rounded border border-[var(--accent-cyan)] bg-[var(--accent-cyan-subtle)] p-2 text-[11px] text-[var(--accent-cyan)]">
        ⏳ 公開開始まで約 {hours} 時間（それまでは「準備中」表示）
      </div>
    );
  }

  // 3) 公開終了済み
  if (endMs && now > endMs) {
    return (
      <div className="rounded border border-[var(--accent-red)] bg-[var(--accent-red-subtle)] p-2 text-[11px] text-[var(--accent-red)]">
        ⛔ 公開終了時刻を過ぎています。終了日を更新するか「期限なし」にしてください。
      </div>
    );
  }

  // 4) 公開中
  return (
    <div className="rounded border border-[var(--accent-green)] bg-[var(--accent-green-subtle)] p-2 text-[11px] text-[var(--accent-green)]">
      ✓ 公開中・誰でもアクセスできます{!endMs ? '（期限なし）' : ''}
    </div>
  );
}
